import React, { useMemo } from 'react';
import styled from 'styled-components/macro';
import {
  ChannelMessageAttachmentType,
  ChannelViewChannelMessageFragment,
  ChannelViewChannelMessageAttachmentFragment,
  MessageType,
  ChannelViewChannelFragment,
} from 'shared/types/graphql';
import MessageBubbleAttachment from './MessageBubbleAttachment';

import he from 'he';
import DevModeDisplayJSON from 'pages/Settings/components/DevModeDisplayJSON';
import MentionedUser from '../../ChannelView/MessageComposer/Mentions/MentionedUser';
import MessageReactions from '../MessageGroup/MessageReactions';
import Linkify from 'linkifyjs/react';

interface IProps {
  message: ChannelViewChannelMessageFragment;
  clinicPetParentId: string;
  setPreviewData: (fileUrl: string, attachmentType: ChannelMessageAttachmentType, fileName: string) => void;
  channel?: ChannelViewChannelFragment | null;
  setCurrentInvoiceId: (invoiceId: string) => void;
  isLastEligibleMessage: boolean;
}

const escapeCharactersForRegex = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

const MessageBubble = ({
  message,
  clinicPetParentId,
  setPreviewData,
  channel,
  setCurrentInvoiceId,
  isLastEligibleMessage,
}: IProps): JSX.Element => {
  const attachments: ChannelViewChannelMessageAttachmentFragment[] =
    message.attachments || message.attributes?.attachments || [];
  const buildMessageBody = useMemo(() => {
    if (!message.body) {
      return <></>;
    }

    const decodedMessageBody = he.decode(message?.body);
    let mentionedUsersString = '';

    message?.mentionedUsers?.forEach((user) => {
      const { nameDisplay, firstName, lastName } = user;
      const name = nameDisplay ? `${nameDisplay}` : `${firstName} ${lastName}`;
      const escapedNameRegex = escapeCharactersForRegex(name);
      mentionedUsersString += `${mentionedUsersString.length ? `|` : ``}@${escapedNameRegex}`;
    });

    const mentionedUsersRegEx = new RegExp(`(${mentionedUsersString})`, 'gi');
    const messageContentsArray = decodedMessageBody.split(mentionedUsersRegEx);

    return (
      <Linkify tagName="span" options={{ target: '_blank', className: 'conversation-link' }}>
        {!!message?.mentionedUsers.length
          ? messageContentsArray?.map((phrase: string, index) => {
              if (!phrase?.length) return;
              if (phrase.indexOf('@') === 0 && mentionedUsersString.includes(escapeCharactersForRegex(phrase))) {
                return <MentionedUser key={`${message.id}_$${phrase}${index}`}>{phrase}</MentionedUser>;
              } else {
                return phrase;
              }
            })
          : decodedMessageBody}
      </Linkify>
    );
  }, [message]);

  return (
    <>
      {!!message.body?.length && (
        <MessageBubbleContainer messageType={message.messageType}>
          <MessageBody data-testid={`message-group-${message.index}`}>{buildMessageBody}</MessageBody>
        </MessageBubbleContainer>
      )}
      {!!attachments?.length && (
        <>
          {attachments.map((attachment: ChannelViewChannelMessageAttachmentFragment, index: number) => {
            return (
              <MessageBubbleContainer key={index} messageType={message.messageType}>
                <MessageBubbleAttachment
                  attachment={attachment}
                  clinicPetParentId={clinicPetParentId}
                  channelId={channel?.id || ''}
                  setPreviewData={setPreviewData}
                  channel={channel}
                  messageType={message.messageType}
                  setCurrentInvoiceId={setCurrentInvoiceId}
                  isLastEligibleMessage={isLastEligibleMessage}
                />
              </MessageBubbleContainer>
            );
          })}
        </>
      )}
      {message.reactions.filter((reaction) => !reaction.deletedAt).length > 0 && (
        <>
          <VerticalSpacer />
          <MessageReactions message={message} showPlacedReactions />
        </>
      )}
      <DevModeDisplayJSON src={message} />
    </>
  );
};

const MessageBubbleContainer = styled.div<{ messageType: MessageType }>`
  display: flex;
  max-width: 100%;
  width: 100%;
  padding-left: ${({ messageType }): string => {
    return messageType === MessageType.Note ? '10px' : '0px';
  }};
  border-left: ${({ messageType }): string => {
    return messageType === MessageType.Note ? '2px solid #dab307' : '#fff';
  }};
  overflow-anchor: none;

  & + & {
    margin-top: 4px;
  }
`;

const MessageBody = styled.div`
  font-size: 16px;
  line-height: 1.5;
  max-width: 60%;
  white-space: pre-wrap;
  word-break: break-word;
  letter-spacing: 0.01em;
`;

const VerticalSpacer = styled.div`
  height: 8px;
`;

export default MessageBubble;
